import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiService } from '@/services/api'
import { FilePermission, FilePermissionLevel } from '@/types'

interface FilePermissionsState {
  byFileId: Record<number, FilePermission[]>
  isLoading: Record<number, boolean>
  error: string | null
}

const initialState: FilePermissionsState = {
  byFileId: {},
  isLoading: {},
  error: null,
}

export const fetchFilePermissions = createAsyncThunk(
  'filePermissions/fetchFilePermissions',
  async (fileId: number) => {
    const permissions = await apiService.getFilePermissions(fileId)
    return { fileId, permissions }
  }
)

export const grantFilePermission = createAsyncThunk(
  'filePermissions/grantFilePermission',
  async ({
    fileId,
    userId,
    groupId,
    permission,
  }: {
    fileId: number
    userId?: number
    groupId?: number
    permission: FilePermissionLevel
  }) => {
    const newPerm = await apiService.grantFilePermission(fileId, {
      user_id: userId,
      group_id: groupId,
      permission,
    })
    return { fileId, permission: newPerm }
  }
)

export const revokeFilePermission = createAsyncThunk(
  'filePermissions/revokeFilePermission',
  async ({
    fileId,
    userId,
    groupId,
  }: {
    fileId: number
    userId?: number
    groupId?: number
  }) => {
    await apiService.revokeFilePermission(fileId, { user_id: userId, group_id: groupId })
    return { fileId, userId, groupId }
  }
)

const filePermissionsSlice = createSlice({
  name: 'filePermissions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilePermissions.pending, (state, action) => {
        state.isLoading[action.meta.arg] = true
        state.error = null
      })
      .addCase(fetchFilePermissions.fulfilled, (state, action) => {
        const { fileId, permissions } = action.payload
        state.isLoading[fileId] = false
        state.byFileId[fileId] = permissions
      })
      .addCase(fetchFilePermissions.rejected, (state, action) => {
        state.isLoading[action.meta.arg] = false
        state.error = action.error.message || 'Failed to load permissions'
      })
      .addCase(grantFilePermission.fulfilled, (state, action) => {
        const { fileId, permission } = action.payload
        const existing = state.byFileId[fileId] || []
        const idx = existing.findIndex(
          (p) =>
            (permission.user_id && p.user_id === permission.user_id) ||
            (permission.group_id && p.group_id === permission.group_id)
        )
        if (idx >= 0) {
          existing[idx] = permission
        } else {
          existing.push(permission)
        }
        state.byFileId[fileId] = existing
      })
      .addCase(revokeFilePermission.fulfilled, (state, action) => {
        const { fileId, userId, groupId } = action.payload
        if (state.byFileId[fileId]) {
          state.byFileId[fileId] = state.byFileId[fileId].filter((p) => {
            if (userId && p.user_id === userId) return false
            if (groupId && p.group_id === groupId) return false
            return true
          })
        }
      })
  },
})

export default filePermissionsSlice.reducer
